<template>
  <section
    class="login"
    @keyup.enter="registerUser">
    <router-link
      :to="{ name: 'LoginPage' }"
      class="pointer"
      tag="span">
      <img
        class="login__logo show-hand"
        style="display: inline-block !important;"
        src="~@/assets/img/SB-Images/app-logo.svg"
        alt="AI Number" >
    </router-link>
    <h1 v-if="!inviteToken">Create AI Number account</h1>
    <h1 v-else>Activate AI Number account</h1>
    <div class="login-additionals">
      <span v-if="!inviteToken">Already have an account?</span>
      <span v-else>By creating account you'll join {{ organisation.name }}.</span>
      <router-link
        v-if="!inviteToken"
        :to="{ name: 'LoginPage', query: $route.query }"
        tag="span"
        class="login__create-acc">
        Sign in
      </router-link>
    </div>
    <ValidationObserver
      ref="customFieldObserver"
      v-slot="{ valid, pristine }" >
      <div class="login-form">

        <!-- full name form field -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="Full name" >
          <b-form-group
            label="Full Name"
            label-for="fullname">
            <b-form-input
              id="fullname"
              v-model="fullname"
              name="fullname"
              type="text"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter full name"
              trim/>
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <!-- email form field -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|email"
          name="Email"
          mode="eager" >
          <b-form-group
            label="Email"
            label-for="email">
            <b-form-input
              id="email"
              v-model="emailValue"
              name="email"
              type="email"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter email"
              :disabled="disableEmail"
              trim/>
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <!-- password form field -->
        <!-- TODO - add additional validation 1 big, 1 small, 1 number - see trello -->
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|min:8"
          name="Password" >
          <b-form-group
            label="Password"
            label-for="password">
            <b-form-input
              id="password"
              v-model="password"
              name="password"
              type="password"
              autocomplete="off"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter password (at least 8 characters)"
              trim/>
            <!-- password validation -->
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>
      </div>

      <div
        v-if="apiError && apiError.data"
        class="main-error flex center-all">
        <SBIcon
          class="mr-2"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="12" />
        <p>{{ apiError.data.message }}</p>
      </div>

      <b-button
        variant="primary"
        :disabled="sending || !valid || pristine"
        :class="{loading: sending}"
        class="flex center-all settings__actions--save"
        block
        @click="registerUser">
        <span>Sign up</span>
        <Spinner
          v-if="sending"
          :size="16"
          :thickness="2"
          :color="SBColors.white" />
      </b-button>

      <div class="small-sub text-center">
        <!-- eslint-disable-next-line -->
        <span>By signing up you accept our <a href="https://ainumber.com/legal.html" target="_blank">TOS</a> and <a href="https://ainumber.com/legal.html#privacy-policy" target="_blank">PP</a>.</span>
      </div>
    </ValidationObserver>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { extend } from 'vee-validate';
import { email, min } from 'vee-validate/dist/rules';
import setValidationErrors from '@/helpers/validationErrors';
import FieldFocusBlur from '@/mixins/FieldFocusBlur';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import Spinner from '@/components/common/Spinner';
import getGeoLocation from '@/helpers/getGeoLocation';

// extend('email', email);
extend('min', {
  ...min,
  message: 'Minimum 8 characters required.',
});

export default {
  name: 'Register',
  components: {
    SBIcon,
    Spinner,
  },
  mixins: [FieldFocusBlur],
  props: {
    inviteToken: {
      type: String,
      required: false,
      default: null,
    },
    email: {
      type: String,
      required: false,
      default: null,
    },
    organisation: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      fullname: '',
      emailValue: '',
      password: '',
      whatsappConsent: true,
      errorMessage: '',
      sending: false,
      disableEmail: false,
      validationErrors: {},
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      authenticated: 'user/authenticated',
      apiError: 'user/apiError',
    }),
  },
  watch: {
    authenticated(auth) {
      if (auth) {
        // this.sending = false;
        if (this.inviteToken && this.inviteToken.length) {
          // this.acceptMerchantInvitation({ invite_token: this.inviteToken });
        }
        // this.fetchMerchantSubscriptions();

        if (this.organisation && this.organisation.id) {
          this.$router.push({ name: 'OrganisationWithId', params: { organisationId: this.organisation.id } });
        } else {
          this.$router.push({ name: 'logged-in-landing-page' });
        }
      }
    },
    apiError(error) {
      this.sending = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  mounted() {
    if (this.email) {
      this.emailValue = this.email;
      this.disableEmail = true;
    }

    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      register: 'user/register',
      acceptMerchantInvitation: 'user/acceptMerchantInvitation',
      // fetchMerchantSubscriptions: 'merchant/fetchSubscriptions',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    registerUser() {
      if (this.$refs.customFieldObserver.flags.valid) {
        this.sending = true;
        const userData = {
          full_name: this.fullname,
          email: this.emailValue,
          password: this.password,
          whatsapp_consent: this.whatsappConsent,
          region: 'US',
        };

        getGeoLocation().then((response) => {
          if (response.status === 200 && response.data.country_code.length) {
            userData.region = response.data.country_code;
          }
        }).catch(() => {
          // console.log('Region unknown.');
        }).finally(() => {
          this.register(userData);
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.settings__actions--save {
  margin-top: 30px;
  height: 44px;
}

.small-sub {
  margin-top: 16px;
  font-size: 12px !important;
  font-weight: 400 !important;
  color: $blackUltralight !important;
}

.api-error {
  margin: 30px 0;
  font-size: 14px;
  font-weight: 400;
  color: #ff5b61;
}
</style>
